import {useState, useEffect} from 'react';
import type {ScreenBreakpoints} from '@Libraries/responsiveness-library';

/**
 * True while the viewport matches the media query. Built on `matchMedia`'s `change` event, which fires
 * only on breakpoint crossings, so an idle consumer costs nothing while the user drags the window.
 */
export const useMatchesMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState((): boolean => {
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const onMediaQueryChange = (event: MediaQueryListEvent): void => {
      setMatches(event.matches);
    };

    mediaQuery.addEventListener('change', onMediaQueryChange);
    return (): void => {
      mediaQuery.removeEventListener('change', onMediaQueryChange);
    };
  }, [query]);

  return matches;
};

/** True while the viewport is at or below the given breakpoint (`max-width: Npx` semantics, i.e. `<=`). */
export const useIsSmallerThanBreakpointWidth = (breakpointWidth: ScreenBreakpoints): boolean => {
  return useMatchesMediaQuery(`(max-width: ${breakpointWidth}px)`);
};

/** True while the viewport is at or above the given breakpoint (`min-width: Npx` semantics, i.e. `>=`). */
export const useIsLargerThanBreakpointWidth = (breakpointWidth: ScreenBreakpoints): boolean => {
  return useMatchesMediaQuery(`(min-width: ${breakpointWidth}px)`);
};

/** True while the viewport is at or below the given height (`max-height: Npx` semantics, i.e. `<=`). */
export const useIsViewportHeightAtMost = (maxHeight: number): boolean => {
  return useMatchesMediaQuery(`(max-height: ${maxHeight}px)`);
};
